import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration as BREIntegration } from '@feathr/blackbox';
import { Alert, CardV2 as Card, toast, Toggle } from '@feathr/components';

import EnableDonationSyncModal from './EnableDonationSyncModal';

import * as styles from './RaisersEdgeSyncActivity.css';

interface IProps {
  integration: BREIntegration;
}

function RaisersEdgeSyncActivity({ integration }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [showEnableModal, setShowEnableModal] = useState(false);
  const downsyncActivities = integration.get('downsync_activities');
  const upsyncActivities = integration.get('upsync_activities');
  const hasFundConfigured = !!integration.get('fundraising')?.fund_id;

  async function handleToggleDownsync(syncValue: boolean): Promise<void> {
    integration.set({
      downsync_activities: { ...downsyncActivities, gift_events: syncValue },
    });
    try {
      await integration.patchDirty();
      toast(
        t(`"{{- field}}" {{action}}`, {
          field: t("Downsync Raiser's Edge NXT Gifts to Feathr"),
          action: syncValue ? 'enabled' : 'disabled',
        }),
        {
          type: 'success',
        },
      );
    } catch (error) {
      toast(t('Something went wrong while updating the setting:\n{{- error}}', { error }), {
        type: 'error',
      });
    }
  }

  function handleToggleUpsync(): void {
    const isCurrentlyEnabled = upsyncActivities?.donations ?? false;

    if (isCurrentlyEnabled) {
      handleDisableUpsync();
    } else {
      setShowEnableModal(true);
    }
  }

  async function handleDisableUpsync(): Promise<void> {
    integration.set({
      upsync_activities: {
        ...integration.get('upsync_activities'),
        donations: false,
      },
    });
    try {
      await integration.patchDirty();
      toast(
        t(`"{{- field}}" {{action}}`, {
          field: t("Upsync Donations to Raiser's Edge NXT"),
          action: t('disabled'),
        }),
        {
          type: 'success',
        },
      );
    } catch (error) {
      toast(t('Something went wrong while updating the setting:\n{{- error}}', { error }), {
        type: 'error',
      });
    }
  }

  function handleCloseModal(): void {
    setShowEnableModal(false);
  }

  function handleEnableSuccess(): void {
    setShowEnableModal(false);
  }

  return (
    <>
      <Card contentClassName={styles.card}>
        <Card.Content contentClassName={styles.content}>
          <div className={styles.textContent}>
            <div className={styles.title}>{t("Downsync Raiser's Edge NXT Gifts to Feathr")}</div>
            <div className={styles.description}>
              {t(
                "When enabled, gifts added to constituents in Raiser's Edge NXT are synced to the corresponding contact in Feathr.",
              )}
            </div>
          </div>
          <Toggle
            disabled={integration.get('has_provisioning_gift_subscriptions')}
            onChange={(): void => {
              handleToggleDownsync(!downsyncActivities?.gift_events);
            }}
            value={downsyncActivities?.gift_events ?? false}
          />
        </Card.Content>
      </Card>

      <Card contentClassName={styles.card}>
        <Card.Content contentClassName={styles.content}>
          <div className={styles.textContent}>
            <div className={styles.title}>{t("Upsync Donations to Raiser's Edge NXT")}</div>
            <div className={styles.description}>
              {t(
                'When enabled, donation form submissions create/update constituent records and gift records in RE NXT. This works independently of the Persons/Constituents sync setting.',
              )}{' '}
              <a href={'https://help.feathr.co'} rel={'noopener noreferrer'} target={'_blank'}>
                {t('Learn more')}
              </a>
            </div>
            {!hasFundConfigured && (
              <Alert
                className={styles.fundWarning}
                description={t('To enable donation syncing, select a fund.')}
                title={t('Fund selection required')}
                type={'warning'}
              >
                <button onClick={(): void => setShowEnableModal(true)} type={'button'}>
                  {t('Select a fund')}
                </button>
              </Alert>
            )}
          </div>
          <Toggle onChange={handleToggleUpsync} value={upsyncActivities?.donations ?? false} />
        </Card.Content>
      </Card>

      <EnableDonationSyncModal
        integration={integration}
        onClose={handleCloseModal}
        onSuccess={handleEnableSuccess}
        opened={showEnableModal}
      />
    </>
  );
}

export default observer(RaisersEdgeSyncActivity);
